import { createServerFn } from "@tanstack/react-start";
import { releaseForReport } from "@/lib/cot-schedule";

// CFTC Disaggregated Futures Only report, COMEX Gold (contract 088691).
// Public dataset, released Fridays 15:30 ET for the prior Tuesday.

export type CotWeek = {
  date: string; // YYYY-MM-DD (Tuesday as-of date)
  openInterest: number;
  mmLong: number;
  mmShort: number;
  mmNet: number;
  commNet: number; // producer/merchant + swap dealers
  otherNet: number;
  retailNet: number; // non-reportable
  mmLongChg: number;
  mmShortChg: number;
  positions: Record<CotGroupId, CotPosition>;
};

export type CotGroupId = "managedMoney" | "producers" | "swapDealers" | "other" | "nonReportable";
export type CotPosition = { long: number; short: number; spreading: number | null };

export type CotDTO = {
  weeks: CotWeek[]; // oldest first
  mmIndex3y: number | null; // 0-100 percentile position of MM net in 3y range
  commIndex3y: number | null;
  mmMin3y: number | null;
  mmMax3y: number | null;
  error?: string;
  stale?: boolean;
};

const URL_BASE = "https://publicreporting.cftc.gov/resource/72hh-3qpy.json";
const FIELDS = [
  "report_date_as_yyyy_mm_dd",
  "open_interest_all",
  "prod_merc_positions_long",
  "prod_merc_positions_short",
  "swap_positions_long_all",
  "swap__positions_short_all",
  "m_money_positions_long_all",
  "m_money_positions_short_all",
  "other_rept_positions_long",
  "other_rept_positions_short",
  "nonrept_positions_long_all",
  "nonrept_positions_short_all",
  "change_in_m_money_long_all",
  "change_in_m_money_short_all",
  "m_money_positions_spread",
  "swap__positions_spread_all",
  "other_rept_positions_spread",
];

const SNAPSHOT_KEY = "cot_gold_v1";

let cache: { at: number; data: CotDTO } | null = null;

// Release-aware cache: the report changes once a week, so the cache holds until
// the next report is due, then polls every 5 minutes for 2 hours, then hourly
// (covers CFTC holiday delays without hammering the feed).
function cacheValid(c: { at: number; data: CotDTO }, now = Date.now()): boolean {
  const latest = c.data.weeks[c.data.weeks.length - 1];
  if (!latest) return now - c.at < 5 * 60_000;
  const nextDue = releaseForReport(latest.date).getTime() + 7 * 86400_000;
  if (now < nextDue) return true;
  const sinceDue = now - nextDue;
  return now - c.at < (sinceDue < 2 * 3600_000 ? 5 * 60_000 : 60 * 60_000);
}

const n = (v: unknown) => {
  if (v == null || v === "") throw new Error("Missing CFTC position field");
  const x = Number(v);
  if (!Number.isFinite(x)) throw new Error("Invalid CFTC position field");
  return x;
};

function indexOf(values: number[], current: number) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (!(max > min)) return { idx: null, min, max };
  return { idx: ((current - min) / (max - min)) * 100, min, max };
}

export const getGoldCot = createServerFn({ method: "GET" }).handler(async (): Promise<CotDTO> => {
  if (cache && cacheValid(cache)) return cache.data;
  // Durable weekly snapshot: every server instance reads the same stored
  // report and only contacts CFTC once the next release is due.
  const { saveSnapshot, loadSnapshot } = await import("@/lib/macro-snapshot.server");
  const stored = await loadSnapshot<{ at: number; data: CotDTO }>(SNAPSHOT_KEY);
  if (stored?.data?.weeks?.length && (!cache || stored.at > cache.at)) cache = stored;
  if (cache && cacheValid(cache)) return cache.data;
  try {
    const qs = new URLSearchParams({
      cftc_contract_market_code: "088691",
      $select: FIELDS.join(","),
      $order: "report_date_as_yyyy_mm_dd DESC",
      $limit: "260",
    });
    const res = await fetch(`${URL_BASE}?${qs}`, { headers: { Accept: "application/json" }, signal: AbortSignal.timeout(15000) });
    if (!res.ok) throw new Error(`CFTC ${res.status}`);
    const rows = (await res.json()) as Record<string, string>[];
    const weeks: CotWeek[] = rows
      .map((r) => {
        const mmLong = n(r.m_money_positions_long_all);
        const mmShort = n(r.m_money_positions_short_all);
        return {
          date: String(r.report_date_as_yyyy_mm_dd).slice(0, 10),
          openInterest: n(r.open_interest_all),
          mmLong,
          mmShort,
          mmNet: mmLong - mmShort,
          commNet:
            n(r.prod_merc_positions_long) +
            n(r.swap_positions_long_all) -
            n(r.prod_merc_positions_short) -
            n(r.swap__positions_short_all),
          otherNet: n(r.other_rept_positions_long) - n(r.other_rept_positions_short),
          retailNet: n(r.nonrept_positions_long_all) - n(r.nonrept_positions_short_all),
          mmLongChg: n(r.change_in_m_money_long_all),
          mmShortChg: n(r.change_in_m_money_short_all),
          positions: {
            managedMoney: { long: mmLong, short: mmShort, spreading: n(r.m_money_positions_spread) },
            producers: { long: n(r.prod_merc_positions_long), short: n(r.prod_merc_positions_short), spreading: null },
            swapDealers: { long: n(r.swap_positions_long_all), short: n(r.swap__positions_short_all), spreading: n(r.swap__positions_spread_all) },
            other: { long: n(r.other_rept_positions_long), short: n(r.other_rept_positions_short), spreading: n(r.other_rept_positions_spread) },
            nonReportable: { long: n(r.nonrept_positions_long_all), short: n(r.nonrept_positions_short_all), spreading: null },
          },
        };
      })
      .reverse();
    if (!weeks.length) throw new Error("No COT rows");
    const last3y = weeks.slice(-156);
    const latest = weeks[weeks.length - 1];
    const mm = indexOf(last3y.map((w) => w.mmNet), latest.mmNet);
    const cm = indexOf(last3y.map((w) => w.commNet), latest.commNet);
    const data: CotDTO = {
      weeks,
      mmIndex3y: mm.idx,
      commIndex3y: cm.idx,
      mmMin3y: mm.min,
      mmMax3y: mm.max,
    };
    const prevLatest = cache?.data.weeks[cache.data.weeks.length - 1]?.date;
    cache = { at: Date.now(), data };
    // Save on a new report, and refresh the stamp while polling so other
    // instances share the same retry cadence.
    if (prevLatest !== latest.date || !stored || Date.now() - stored.at > 4 * 60_000) await saveSnapshot(SNAPSHOT_KEY, cache);
    return data;
  } catch (e) {
    console.error("[cot]", e);
    if (cache) return { ...cache.data, stale: true };
    return { weeks: [], mmIndex3y: null, commIndex3y: null, mmMin3y: null, mmMax3y: null, error: "COT data unavailable" };
  }
});
